import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { billingAPI } from '../api';
import { toast } from 'sonner';
import { 
  ArrowLeft, Crown, Star, Sparkles, Check, X, Zap, 
  Users, Infinity, Shield, Award, Clock, Heart,
  Loader2, AlertCircle, Gift, Rocket
} from 'lucide-react';

const BillingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [founderStatus, setFounderStatus] = useState(null);
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    loadBillingData();
    
    // Check for success/cancel from Stripe redirect
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      handleCheckoutReturn(sessionId);
    }
  }, [searchParams]);

  const loadBillingData = async () => {
    setLoading(true);
    try {
      const [statusRes, founderRes, plansRes] = await Promise.all([
        billingAPI.getSubscriptionStatus(),
        billingAPI.getFounderStatus(),
        billingAPI.getPlans()
      ]);
      
      setSubscriptionStatus(statusRes.data);
      setFounderStatus(founderRes.data);
      setPlans(plansRes.data?.plans || []);
    } catch (error) {
      console.error('Error loading billing data:', error);
      toast.error('Failed to load billing information');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckoutReturn = async (sessionId) => {
    try {
      const res = await billingAPI.getCheckoutStatus(sessionId);
      if (res.data?.payment_status === 'paid') {
        toast.success('Payment successful! Welcome to the family!');
        // Reload billing data to show new status
        await loadBillingData();
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
  };

  const handleUpgrade = async (planId) => {
    setCheckoutLoading(planId);
    try {
      const originUrl = window.location.origin;
      const res = await billingAPI.createCheckoutSession(planId, originUrl);
      
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to start checkout';
      toast.error(message);
    } finally {
      setCheckoutLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-indigo-950 to-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-indigo-400 animate-spin" />
      </div>
    );
  }

  const isFounder = subscriptionStatus?.founder_lifetime;
  const hasPremium = subscriptionStatus?.has_premium;
  const foundersSoldOut = founderStatus?.is_sold_out;
  const foundersRemaining = founderStatus?.founders_remaining || 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-indigo-950 to-slate-900">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-slate-900/80 backdrop-blur-lg border-b border-slate-700/50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          <h1 className="text-xl font-bold text-white">Choose Your Plan</h1>
          <div className="w-20" />
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8 sm:py-12">
        {/* Current Status Banner */}
        {hasPremium && (
          <div className={`mb-8 p-6 rounded-2xl border ${
            isFounder 
              ? 'bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-amber-500/30' 
              : 'bg-gradient-to-r from-indigo-500/20 to-purple-500/20 border-indigo-500/30'
          }`}>
            <div className="flex items-center gap-4">
              {isFounder ? (
                <>
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                    <Crown className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-2xl font-bold text-amber-300">
                        Founding Family #{subscriptionStatus?.founder_number}
                      </h2>
                      <span className="px-2 py-0.5 bg-amber-500/30 text-amber-300 text-xs rounded-full font-medium">
                        FOUNDER
                      </span>
                    </div>
                    <p className="text-amber-200/70 mt-1">Lifetime access • Unlimited activities</p>
                    {subscriptionStatus?.constellation_reward && (
                      <div className="mt-2 flex items-center gap-2 text-amber-300">
                        <Sparkles className="w-4 h-4" />
                        <span>Your Constellation: <strong>{subscriptionStatus.constellation_reward}</strong></span>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center">
                    <Star className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-indigo-300">{subscriptionStatus?.plan_name}</h2>
                    <p className="text-indigo-200/70 mt-1">Unlimited activities for your family</p>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Hero Section */}
        {!hasPremium && (
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Unlock the Full Temple Experience
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              Give your children unlimited access to learning adventures, creative tools, and magical experiences.
            </p>
          </div>
        )}

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {/* Free Tier */}
          <div className="relative bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6 flex flex-col">
            <div className="mb-6">
              <div className="w-12 h-12 rounded-xl bg-slate-700/50 flex items-center justify-center mb-4">
                <Gift className="w-6 h-6 text-slate-400" />
              </div>
              <h3 className="text-xl font-bold text-white">Free</h3>
              <p className="text-slate-400 text-sm mt-1">Perfect for trying out</p>
            </div>
            
            <div className="mb-6">
              <div className="text-4xl font-bold text-white">$0</div>
              <div className="text-slate-500 text-sm">forever free</div>
            </div>
            
            <ul className="space-y-3 mb-6 flex-1">
              <li className="flex items-center gap-3 text-slate-300">
                <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                <span>5 activities per day per child</span>
              </li>
              <li className="flex items-center gap-3 text-slate-300">
                <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                <span>Access to all learning rooms</span>
              </li>
              <li className="flex items-center gap-3 text-slate-300">
                <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                <span>Basic creative tools</span>
              </li>
              <li className="flex items-center gap-3 text-slate-500">
                <X className="w-5 h-5 text-slate-600 flex-shrink-0" />
                <span>Unlimited activities</span>
              </li>
            </ul>
            
            <button
              disabled
              className="w-full py-3 bg-slate-700/50 text-slate-400 rounded-xl font-medium cursor-not-allowed"
            >
              Current Plan
            </button>
          </div>

          {/* Family Monthly - Best Value */}
          <div className="relative bg-gradient-to-b from-indigo-900/50 to-slate-800/50 border-2 border-indigo-500/50 rounded-2xl p-6 flex flex-col transform md:scale-105 z-10">
            {/* Best Value Badge */}
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <span className="px-4 py-1.5 bg-gradient-to-r from-indigo-500 to-purple-500 text-white text-sm font-bold rounded-full shadow-lg">
                BEST VALUE
              </span>
            </div>
            
            <div className="mb-6 mt-2">
              <div className="w-12 h-12 rounded-xl bg-indigo-500/30 flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-indigo-400" />
              </div>
              <h3 className="text-xl font-bold text-white">Family Plan</h3>
              <p className="text-indigo-300 text-sm mt-1">Unlimited for everyone</p>
            </div>
            
            <div className="mb-6">
              <div className="text-4xl font-bold text-white">$6.99</div>
              <div className="text-indigo-300 text-sm">per month</div>
            </div>
            
            <ul className="space-y-3 mb-6 flex-1">
              <li className="flex items-center gap-3 text-white">
                <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                <span className="font-medium">Unlimited activities</span>
              </li>
              <li className="flex items-center gap-3 text-white">
                <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                <span>All children included</span>
              </li>
              <li className="flex items-center gap-3 text-white">
                <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                <span>Full creative tools access</span>
              </li>
              <li className="flex items-center gap-3 text-white">
                <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                <span>Priority support</span>
              </li>
              <li className="flex items-center gap-3 text-white">
                <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                <span>Cancel anytime</span>
              </li>
            </ul>
            
            <button
              onClick={() => handleUpgrade('family_monthly')}
              disabled={hasPremium || checkoutLoading === 'family_monthly'}
              className="w-full py-3 bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 disabled:from-slate-600 disabled:to-slate-600 text-white rounded-xl font-medium transition-all flex items-center justify-center gap-2"
              data-testid="upgrade-family-btn"
            >
              {checkoutLoading === 'family_monthly' ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : hasPremium ? (
                'Already Subscribed'
              ) : (
                <>
                  <Zap className="w-5 h-5" />
                  Subscribe Now
                </>
              )}
            </button>
          </div>

          {/* Founder Lifetime */}
          <div className={`relative bg-gradient-to-b from-amber-900/30 to-slate-800/50 border-2 rounded-2xl p-6 flex flex-col ${
            foundersSoldOut ? 'border-slate-600/50 opacity-75' : 'border-amber-500/50'
          }`}>
            {/* Limited Badge */}
            {!foundersSoldOut && (
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="px-4 py-1.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-bold rounded-full shadow-lg flex items-center gap-1">
                  <Rocket className="w-4 h-4" />
                  LIMITED
                </span>
              </div>
            )}
            
            <div className="mb-6 mt-2">
              <div className="w-12 h-12 rounded-xl bg-amber-500/30 flex items-center justify-center mb-4">
                <Crown className="w-6 h-6 text-amber-400" />
              </div>
              <h3 className="text-xl font-bold text-white">Founder Lifetime</h3>
              <p className="text-amber-300 text-sm mt-1">
                {foundersSoldOut 
                  ? 'Sold Out' 
                  : `Only ${foundersRemaining} spots left!`
                }
              </p>
            </div>
            
            <div className="mb-6">
              <div className="text-4xl font-bold text-white">$39</div>
              <div className="text-amber-300 text-sm">one-time payment</div>
            </div>
            
            <ul className="space-y-3 mb-6 flex-1">
              <li className="flex items-center gap-3 text-white">
                <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                <span className="font-medium">Lifetime access</span>
              </li>
              <li className="flex items-center gap-3 text-white">
                <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                <span>Unlimited activities forever</span>
              </li>
              <li className="flex items-center gap-3 text-amber-300">
                <Award className="w-5 h-5 text-amber-400 flex-shrink-0" />
                <span className="font-medium">Founder Badge</span>
              </li>
              <li className="flex items-center gap-3 text-amber-300">
                <Star className="w-5 h-5 text-amber-400 flex-shrink-0" />
                <span className="font-medium">Founding Family Number</span>
              </li>
              <li className="flex items-center gap-3 text-amber-300">
                <Sparkles className="w-5 h-5 text-amber-400 flex-shrink-0" />
                <span className="font-medium">Unique Constellation</span>
              </li>
            </ul>
            
            {foundersSoldOut ? (
              <div className="w-full py-3 bg-slate-700/50 text-slate-400 rounded-xl font-medium text-center">
                <div className="flex items-center justify-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  Founding Families Filled
                </div>
              </div>
            ) : (
              <button
                onClick={() => handleUpgrade('founder_lifetime')}
                disabled={hasPremium || checkoutLoading === 'founder_lifetime'}
                className="w-full py-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 disabled:from-slate-600 disabled:to-slate-600 text-white rounded-xl font-medium transition-all flex items-center justify-center gap-2"
                data-testid="upgrade-founder-btn"
              >
                {checkoutLoading === 'founder_lifetime' ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : hasPremium ? (
                  'Already a Member'
                ) : (
                  <>
                    <Crown className="w-5 h-5" />
                    Become a Founder
                  </>
                )}
              </button>
            )}
            
            {!foundersSoldOut && !hasPremium && (
              <p className="text-center text-amber-400/70 text-xs mt-3">
                Limited to the first 100 families
              </p>
            )}
          </div>
        </div>

        {/* Founder Progress Bar */}
        {!foundersSoldOut && !hasPremium && (
          <div className="max-w-md mx-auto mb-12">
            <div className="flex justify-between text-sm text-slate-400 mb-2">
              <span>Founding Families claimed</span>
              <span className="text-amber-400">{100 - foundersRemaining}/100</span>
            </div>
            <div className="h-3 bg-slate-800 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-amber-500 to-orange-500 transition-all duration-500"
                style={{ width: `${((100 - foundersRemaining) / 100) * 100}%` }}
              />
            </div>
            <p className="text-center text-amber-400/70 text-sm mt-2">
              {foundersRemaining} spots remaining
            </p>
          </div>
        )}

        {/* FAQ Section */}
        <div className="max-w-2xl mx-auto">
          <h3 className="text-xl font-bold text-white text-center mb-6">Frequently Asked Questions</h3>
          <div className="space-y-4">
            <div className="bg-slate-800/50 rounded-xl p-4">
              <h4 className="font-medium text-white mb-2">What's included in the free tier?</h4>
              <p className="text-slate-400 text-sm">
                Each child can complete up to 5 activities per day, including lessons, games, and creative activities.
              </p>
            </div>
            <div className="bg-slate-800/50 rounded-xl p-4">
              <h4 className="font-medium text-white mb-2">What makes Founders special?</h4>
              <p className="text-slate-400 text-sm">
                Founding Families get lifetime access, a unique founder number (1-100), a special badge, 
                and their own constellation in Temple Sky. These rewards are exclusive to our first 100 families.
              </p>
            </div>
            <div className="bg-slate-800/50 rounded-xl p-4">
              <h4 className="font-medium text-white mb-2">Can I cancel my subscription?</h4>
              <p className="text-slate-400 text-sm">
                Yes! The Family Plan can be cancelled anytime. You'll keep access until the end of your billing period.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingPage;
